/**
 * Pure helpers for dropping chats that were deleted on another device.
 *
 * The native sidebar is a persisted cache that *merges* server pages rather
 * than replacing the full list. The live `conversation_deleted` socket event
 * only reaches connected clients, so a phone that was backgrounded keeps
 * showing those rows until we reconcile against the server.
 */

export const OBJECT_ID_RE = /^[0-9a-f]{24}$/i;
export const EXISTING_CHAT_IDS_BATCH = 500;

export function isPersistedChatObjectId(id) {
  return typeof id === 'string' && OBJECT_ID_RE.test(id);
}

/** Local ids we can ask the server about (owned, persisted ObjectIds). */
export function collectOwnedChatIds(chats) {
  const ids = [];
  const seen = new Set();
  for (const chat of chats || []) {
    const id = chat?.id;
    if (!isPersistedChatObjectId(id) || seen.has(id)) continue;
    // Shared/public chats the viewer doesn't own are absent from *this*
    // user's conversation collection — asking about them would look like a
    // delete and blank the shared-chat view.
    if (chat.isOwner === false) continue;
    seen.add(id);
    ids.push(id);
  }
  return ids;
}

/**
 * Keep chats the server still has, plus rows we did not (or cannot) check:
 * optimistic local ids, shared chats, and ids from a batch that failed.
 */
export function filterChatsAgainstExistingIds(chats, existingIds, checkedIds) {
  const alive = existingIds instanceof Set ? existingIds : new Set(existingIds || []);
  const checked = checkedIds instanceof Set ? checkedIds : new Set(checkedIds || []);
  return (chats || []).filter((chat) => {
    const id = chat?.id;
    if (!isPersistedChatObjectId(id)) return true;
    if (chat.isOwner === false) return true;
    if (!checked.has(id)) return true;
    return alive.has(id);
  });
}

function parseChatTime(chat) {
  const t = Date.parse(chat?.updatedAt || chat?.lastMessageTime || chat?.createdAt || '');
  return Number.isNaN(t) ? null : t;
}

function keepOutsideDefaultListing(chat) {
  if (chat.isOwner === false) return true;
  if (!isPersistedChatObjectId(chat?.id)) return true;
  if (chat?.flags?.isTemporary === true) return true;
  if (chat.isArchived) return true;
  if (chat.inCollectionId) return true;
  if (chat.isInferencing || chat.isInferencingWaiting) return true;
  if (chat?.flags?.isToolRun === true && !chat?.flags?.isToolPromotedToChat) return true;
  return false;
}

/**
 * Fallback prune when POST /json/conversations/existing-ids is unavailable.
 *
 * Page 1 of the default listing is sorted by -updatedAt. A local chat whose
 * updatedAt falls inside that window but is missing from the response is gone
 * from that listing. Pinned chats *are* in the default listing, so they are
 * eligible. Archived / in-collection / temporary rows are not, so they stay.
 *
 * An empty page 1 with `hasMore: false` means the entire listing is empty —
 * every owned chat in the window (all of them) can be dropped.
 */
export function filterChatsAgainstListingWindow({
  localChats,
  serverChats,
  pagination,
  plusLock,
} = {}) {
  if (plusLock) return localChats;
  if (!Array.isArray(serverChats) || !Array.isArray(localChats)) return localChats;

  const listingComplete = pagination?.hasMore === false;
  let windowStart;
  if (listingComplete) {
    windowStart = -Infinity;
  } else {
    if (serverChats.length === 0) return localChats;
    const times = serverChats.map(parseChatTime).filter((t) => t != null);
    if (times.length === 0) return localChats;
    windowStart = Math.min(...times);
  }

  const serverIds = new Set(serverChats.map((c) => c.id));
  return localChats.filter((chat) => {
    if (serverIds.has(chat.id)) return true;
    if (keepOutsideDefaultListing(chat)) return true;
    const t = parseChatTime(chat);
    if (t == null) return true;
    return t < windowStart;
  });
}
